import { useEffect, useRef, useState } from "react";
import { ArrowRight, PlayCircle, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import heroImg from "@/assets/client-design/hero-checkout.png";
import { SHOPIFY_APP_STORE_URL } from "@/lib/appLinks";

const HERO_VIDEO_URL = "/splitkart-screen-recording.mp4";

export function Hero() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showPlayButton, setShowPlayButton] = useState(true);
  const [videoSource, setVideoSource] = useState(HERO_VIDEO_URL);
  const [videoLoading, setVideoLoading] = useState(false);

  useEffect(() => {
    const isAppleMobile =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);

    if (!isAppleMobile) return;

    let cancelled = false;
    let objectUrl = "";

    // The production server does not currently honor MP4 byte-range requests.
    // A small local Blob lets iPhone/iPad Safari play reliably regardless.
    fetch(HERO_VIDEO_URL, { cache: "force-cache" })
      .then((response) => {
        if (!response.ok) throw new Error(`Video request failed with ${response.status}`);
        return response.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(new Blob([blob], { type: "video/mp4" }));
        if (videoRef.current && !videoRef.current.paused) {
          URL.revokeObjectURL(objectUrl);
          objectUrl = "";
          return;
        }
        setVideoSource(objectUrl);
      })
      .catch(() => {
        // Keep the normal URL as a fallback if the Blob preload fails.
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, []);

  const playHeroVideo = async () => {
    const video = videoRef.current;
    if (!video) return;

    setVideoLoading(true);
    video.setAttribute("playsinline", "true");
    video.setAttribute("webkit-playsinline", "true");
    video.muted = false;
    if (video.ended) video.currentTime = 0;

    try {
      await video.play();
      setShowPlayButton(false);
    } catch {
      setShowPlayButton(true);
    } finally {
      setVideoLoading(false);
    }
  };

  return (
    <section className="relative overflow-hidden bg-background">
      <div className="absolute inset-0 bg-gradient-hero" aria-hidden />
      <div className="container-tight relative pt-10 pb-10 md:pt-14 md:pb-14">
        <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="animate-fade-up lg:col-span-5">
            <span className="eyebrow">
              <span className="h-1.5 w-1.5 rounded-full bg-success" />
              Now on Shopify App Store
            </span>
            <h1 className="mt-4 text-balance text-4xl font-bold leading-[1.05] text-foreground md:text-5xl lg:text-[3rem]">
              Unlock high-ticket sales & slash cart abandonment by letting customers{" "}
              <span className="text-primary">split costs at checkout.</span>
            </h1>
            <p className="mt-4 max-w-xl text-pretty text-base leading-relaxed text-muted-foreground md:text-lg">
              Effortless group gifting and split payments at checkout.
            </p>
            <div className="mt-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
              <Button asChild className="group h-12 rounded-lg px-5 text-sm font-semibold">
                <a href={SHOPIFY_APP_STORE_URL} target="_blank" rel="noreferrer">
                  Add to Shopify — Start Free Trial
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </a>
              </Button>
              <a href="/how-it-works" className="text-sm font-semibold text-foreground hover:text-primary">
                See how it works →
              </a>
            </div>
            <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
              <ShieldCheck className="h-4 w-4 text-success" />
              No credit checks · No interest · No customer debt
            </div>
          </div>

          <div className="relative animate-fade-up [animation-delay:120ms] lg:col-span-7">
            <div className="absolute inset-x-8 -bottom-6 h-24 rounded-full bg-primary/20 blur-3xl" aria-hidden />
            <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-elevated lg:scale-[1.03] lg:origin-center">
              <video
                ref={videoRef}
                src={videoSource}
                controls
                playsInline
                preload="auto"
                poster={heroImg}
                aria-label="Splitkart screen recording showing split payments at checkout"
                className="block h-auto w-full"
                onPlay={() => {
                  setVideoLoading(false);
                  setShowPlayButton(false);
                }}
                onPause={() => setShowPlayButton(true)}
                onEnded={() => setShowPlayButton(true)}
              />
              {showPlayButton && (
                <button
                  type="button"
                  onClick={playHeroVideo}
                  disabled={videoLoading}
                  className="absolute inset-0 flex items-center justify-center bg-foreground/10 text-background transition-colors hover:bg-foreground/20"
                  aria-label="Play Splitkart demo video"
                >
                  <span className="flex items-center gap-3 rounded-full bg-foreground/90 px-5 py-3 text-sm font-semibold text-background shadow-elevated">
                    <PlayCircle className="h-7 w-7" />
                    {videoLoading ? "Loading video..." : "Play video with sound"}
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
